"use client";

import { useEffect } from "react";
import { Button } from "@/components/ui/button";

interface RouteErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
  /** Short label for the area that failed, e.g. "dashboard". */
  area?: string;
  title?: string;
  description?: string;
}

/**
 * Shared error-boundary UI for App Router `error.tsx` files. Logs the error and
 * offers a retry (re-renders the segment) plus a home link.
 */
export function RouteError({
  error,
  reset,
  area,
  title = "Something went wrong",
  description = "An unexpected error occurred. You can try again, or head back home.",
}: RouteErrorProps) {
  useEffect(() => {
    // Surface to the console (and any attached error reporting) for debugging.
    console.error(`[RouteError${area ? `:${area}` : ""}]`, error);
  }, [error, area]);

  return (
    <div className="flex flex-col h-full min-h-[60vh] items-center justify-center text-center p-6">
      <div className="container max-w-md">
        <h1 className="text-2xl font-bold mb-2">{title}</h1>
        <p className="text-base text-black-700 mb-2">{description}</p>
        {error?.digest && (
          <p className="text-small2 text-black-500 mb-4 font-mono">
            Reference: {error.digest}
          </p>
        )}
        <div className="flex items-center justify-center gap-3 mt-4">
          <Button variant="primary" onClick={() => reset()}>
            Try again
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              window.location.href = "/";
            }}
          >
            Go home
          </Button>
        </div>
      </div>
    </div>
  );
}

export default RouteError;
